/**
 * This file contains many functions that make various calls to our API and return the promises from those calls.
 */

import axios from 'axios';
import { assign, first, flatMap, get, keys, omit, range } from 'lodash';
import queryString from 'querystring';
import { eventChannel, END } from 'redux-saga';
import { toast } from 'react-toastify';

import Api from '../api.ts';
import { batchProcess } from '../Utils/batchProcess.ts';

const SIGNUP = '/signup';
const ACCESS_TOKEN = '/token';
const LOGIN = '/authentication';
const RECAPTCHA = '/recaptcha';
const USERS = '/users';
const USER = `${LOGIN}/onelogin/user`;
const LOGOUT = `${LOGIN}/onelogin/logout`;

const UserAPI = {
  /**
   * Makes call to endpoint to get a list of users that satisfy the passed params
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  users: [{id: 1, vendor_id: 2, ...}, {id: 2, vendor_id: 3, ...}...]
   *  count: 22
   * }
   */
  get(params = '') {
    return Api.get(`${USERS}?${params}`);
  },

  getOne(id) {
    return Api.get(`${USERS}/${id}`);
  },

  /**
   * Makes call to endpoint to update a user
   * @param {object} data the update to be made to the user
   *
   * @returns {promise} promise returned from the api call
   */
  update(data) {
    return Api.put(`${USERS}/${data.id}`, { data });
  },

  /**
   * Makes call to endpoint to create a user when sign up is submitted
   * @param {object} data the sign up form data
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'mhi',
   *    ...
   *  }
   * }
   */
  signup(data = {}) {
    return Api.post(SIGNUP, { data });
  },

  /**
   * Makes call to endpoint to get the access token
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  token: {token: '1234'},
   *  accessToken: 'token'
   * }
   */
  getToken(token) {
    const data = { token };
    return Api.post(ACCESS_TOKEN, { data });
  },

  /**
   * Makes call to api endpoint to get the current user
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  currentUser: {
   *    id: 1,
   *    ...
   *  }
   * }
   */
  getCurrentUser() {
    return Api.get(`${USERS}/current`);
  },

  /**
   * Makes call to api endpoint to get the current OneLogin user
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  currentUser: {
   *    id: 1,
   *    ...
   *  }
   * }
   */
  getCurrentOneLoginUser() {
    return Api.get(`${USER}`);
  },

  /**
   * Makes call to api endpoint to logout current OneLogin user
   */
  logoutOneLoginUser() {
    return Api.post(`${LOGOUT}`);
  },

  /**
   * Makes call to api endpoint to validate the recaptcha token
   * @param {string} token recaptcha token
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  "success": true,
   *  "score": number,
   *  "action": string,
   *  "challenge_ts": timestamp,
   *  "hostname": string,
   *  "error-codes": [...]
   * }
   */
  validateToken(token) {
    const data = { token };
    return Api.post(RECAPTCHA, { data });
  },

  /**
   * Makes call to google recaptcha api to verify activity
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  token: 'recaptcha_token'
   * }
   */
  validateRecaptcha() {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    return window.grecaptcha.execute(siteKey, { action: 'recaptcha' });
  },

  /**
   * Makes call to api endpoint to log a user in
   * @param {string} email the email entered
   * @param {string} password the password entered
   * @param {string?} token2FA the token for 2FA
   * @param {string?} method2FA the method used to get the 2FA token
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  user: {
   *    id: 1,
   *    ...
   *  },
   *  token: 'asdfasfginionf'
   * }
   */
  login(email, password, token2FA, method2FA) {
    return Api.post(LOGIN, {
      data: {
        email,
        password,
        token2FA,
        method2FA,
      },
    });
  },

  /**
   * Makes call to api endpoint to get the current user's vendor information
   * @param {string} userId the current user's ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'Crowe's Candy',
   *    ...
   *  }
   * }
   */
  getVendor(userId) {
    return Api.get(`${USERS}/${userId}/vendor`);
  },

  /**
   * Makes call to api endpoint to update the vendor record
   * @param {string} userId the current user's ID
   * @param {object} formData the data being updated on the vendor record
   * @param {boolean} fileUpload tells whether updated fields are file fields
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'Crowe's Candy',
   *    ...
   *  }
   * }
   */
  async updateVendor(userId, formData, fileUpload = false) {
    if (!fileUpload) {
      return Api.put(`${USERS}/${userId}/vendor`, { data: formData });
    }

    const response = await Api.get(`${USERS}/${userId}/vendor`);
    const name = first(keys(formData));
    const type = 'vendors';
    const { id } = formData;
    const data = { name, type, id };

    if (get(response, `vendor.${name}.exists`)) {
      await Api.post(`${USERS}/${userId}/archive-file`, { data });
    }

    return eventChannel((emitter) => {
      axios({
        method: 'put',
        url: get(response, `vendor.${name}.write`),
        data: formData[name],
        headers: {
          'Content-Type': 'application/pdf',
        },
        onUploadProgress: (progressEvent) => {
          emitter({ progressEvent, fileName: name });
        },
      })
        .then(() => {
          toast('File successfully uploaded', { type: 'success' });
          emitter({ data: response });
          Api.post(`${USERS}/${userId}/vendor/ns-update`, {
            data: {
              update: {
                file: name,
              },
            },
          });
          emitter(END);
        })
        .catch((error) => {
          console.log('File upload error: ', error);
          toast('Something went wrong in file upload', {
            type: 'error',
          });
          emitter(END);
        });
      return () => {};
    });
  },

  /**
   * Makes call to api endpoint to update the vendor banking
   * @param {string} userId the current user's ID
   * @param {object} formData the data being updated on the vendor record
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    bank_name: 'AXA',
   *    ...
   *  }
   * }
   */
  async updateVendorBanking(userId, formData) {
    return Api.put(`${USERS}/${userId}/vendor/banking`, { data: formData });
  },

  /**
   * Makes call to api endpoint to create vendor record in NetSuite
   * @param {string} userId the current user's ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'Crowe's Candy',
   *    ...
   *  }
   * }
   */
  submitVendor(userId) {
    return Api.post(`${USERS}/${userId}/vendor/submit`, {});
  },

  /**
   * Makes call to api endpoint to create vendor record in NetSuite
   * @param {string} userId the current user's ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'Crowe's Candy',
   *    ...
   *  }
   * }
   */
  createVendorNetSuite(userId) {
    return Api.post(`${USERS}/${userId}/vendor/ns-create`, {});
  },

  /**
   * Makes call to api endpoint to udpate vendor record in NetSuite
   * @param {string} userId the current user's ID
   * @param {object} vendor update on vendor object
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  vendor: {
   *    id: 1,
   *    legal_name: 'Crowe's Candy',
   *    ...
   *  }
   * }
   */
  updateVendorNetSuite(userId, vendor) {
    return Api.post(`${USERS}/${userId}/vendor/ns-update`, {
      data: { update: vendor },
    });
  },

  /**
   * Makes call to api endpoint to grab deliverable purchase orders
   *
   * @param userId
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrders: [POobject1, POobject2, ...]
   * }
   */
  getDeliverablePurchaseOrders(userId, location_id, params) {
    return Api.get(
      `${USERS}/${userId}/purchase-orders/deliverable?location_id=${location_id}&${queryString.encode(
        params,
      )}`,
    );
  },

  /**
   * Makes call to api endpoint to get all purchase orders associated
   * with the current user's vendor
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrders: [POobject1, POobject2, ...]
   * }
   */
  getPurchaseOrders(userId, params) {
    return Api.get(`${USERS}/${userId}/purchase-orders?${params}`);
  },

  /**
   * Makes call to api endpoint to get purchase orders count and quantity
   * with the current user's vendor
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  count: 5,
   *  quantity: 10
   * }
   */
  getPurchaseOrdersCount(userId, params) {
    return Api.get(`${USERS}/${userId}/purchase-orders/count?${params}`);
  },

  /**
   * Makes call to api endpoint to get purchase orders count and quantity
   * with the current user's vendor
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: [{
   *  count: "5",
   *  sub_stage: "Pending Acceptance"
   * },{
   *  count: "3",
   *  sub_stage: "Fully Billed"
   * }]
   */
  getPurchaseOrdersSubStageCount(userId, params) {
    return Api.get(
      `${USERS}/${userId}/purchase-orders/count-sub-stage?${params}`,
    );
  },

  /**
   * Get purchase orders export
   *
   * @param userId
   *
   * @returns [promise] promise returned from api call that eventually
   * resolves to an array of purchase orders.
   *
   * example: purchaseOrders: [purchaseOrder1, purchaseOrder2, ...]
   */
  async getPurchaseOrdersExport(userId, additionalParams) {
    const size = 500;
    const firstPage = await Api.get(
      `${USERS}/${userId}/purchase-orders?${queryString.encode({
        ...additionalParams,
        page: 0,
        size,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / size);

    const remainingOrders = await Promise.all(
      range(1, totalPages).map(async (page) => {
        const params = queryString.encode({
          ...additionalParams,
          page,
          size,
        });
        return Api.get(`${USERS}/${userId}/purchase-orders?${params}`);
      }),
    );

    return [
      ...firstPage.purchaseOrders,
      ...flatMap(remainingOrders, 'purchaseOrders'),
    ];
  },

  /**
   * Makes call to api endpoint to get a single purchase order's details
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrder: {
   *    id: 1,
   *    due_date: '9/24/2020',
   *    ...
   *  }
   * }
   */
  getPurchaseOrder(userId, purchaseOrderId) {
    return Api.get(`${USERS}/${userId}/purchase-orders/${purchaseOrderId}`);
  },

  /**
   * Makes call to api endpoint to get a single purchase order's chat messages.
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   */
  getPurchaseOrderChat(userId, purchaseOrderId) {
    return Api.get(
      `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/chat`,
    );
  },

  /**
   * Makes call to api endpoint to get all vendor bills for the current user
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   *  example: {
   *  vendorBills: [VBobject1, VBobject2, ...]
   * }
   */
  getVendorBills(userId, params) {
    return Api.get(`${USERS}/${userId}/vendor-bills?${params}`);
  },

  getVendorBill(userId, vendorBillId) {
    return Api.get(`${USERS}/${userId}/vendor-bills/${vendorBillId}`);
  },

  /**
   * Get vendor bills export
   *
   * @param userId
   *
   * @returns [promise] promise returned from api call that eventually
   * resolves to an array of vendor bills.
   *
   * example: vendorBills: [vendorBill1, vendorBill2, ...]
   */
  async getVendorBillsExport(userId) {
    const limit = 100;
    const firstPage = await Api.get(
      `${USERS}/${userId}/vendor-bills?${queryString.encode({
        limit: 1,
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const bills = await batchProcess({
      jobs: range(totalPages).map((page) => async () => {
        const params = queryString.encode({ page: page + 1, limit });
        return Api.get(`${USERS}/${userId}/vendor-bills?${params}`);
      }),
      batchSize: 5,
    });

    return flatMap(bills, 'vendorBills');
  },

  /**
   * Makes call to api endpoint to update a purchase order to be accepted
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrder: {
   *    id: 1,
   *    due_date: '9/24/2020',
   *    ...
   *  }
   * }
   */
  acceptPurchaseOrder(userId, purchaseOrderId) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/ns-accept`;
    return Api.post(url);
  },

  /**
   * Marks the specified purchase order as TOP shipped for the given user.
   *
   * @param {string} userId - The ID of the user.
   * @param {string} poNumber - The po_number of the purchase order.
   * @returns {Promise} - A promise that resolves to the response of the API call.
   */
  markTOPShipped(userId, poNumber) {
    const url = `${USERS}/${userId}/purchase-orders/${poNumber}/mark-top-shipped`;
    return Api.patch(url);
  },

  /**
   * Marks the specified purchase order line as CA Prop 65 as yes or no.
   *
   * @param {string} userId - The ID of the user.
   * @param {string} poNumber - The po_number of the purchase order.
   * @param {object} author - The author of the update.
   * @param {object} updates - The updates to the purchase order line.
   * @returns {Promise} - A promise that resolves to the response of the API call.
   */
  setProp65(userId, poNumber, updates) {
    const url = `${USERS}/${userId}/purchase-orders/${poNumber}/prop65`;
    const data = {
      updates,
    };
    return Api.patch(url, { data });
  },

  /**
   * Makes call to api endpoint to update a purchase order to be rejected
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   * @param {string} vendor_rejection_reason the reason the vendor gives for rejecting PO
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrder: {
   *    id: 1,
   *    due_date: '9/24/2020',
   *    ...
   *  }
   * }
   */
  rejectPurchaseOrder(userId, purchaseOrderId, vendor_rejection_reason) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/ns-reject`;
    const data = { vendor_rejection_reason };
    return Api.post(url, { data });
  },

  /**
   * Makes call to api endpoint to update a purchase order in the bulk stage status
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   * @param {string} status the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  purchaseOrder: {
   *    id: 1,
   *    due_date: '9/24/2020',
   *    ...
   *  }
   * }
   */
  updatePoBulkProductionSubStage(userId, purchaseOrderId, sub_stage) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/bulk-prod-update`;
    const data = { sub_stage };
    return Api.post(url, { data });
  },

  /**
   * Makes call to api endpoint to create or update a PO revision
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   * @param {Pick<PurchaseOrderRevisionUpdates, 'updates'>} data the updates of the revision
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  po_number: 'PO1234',
   *  revision: {
   *    status: 'REVISION_REQUESTED',
   *    updates: { ... },
   *    ...,
   *  },
   *  history: [{ ... }, ...],
   * }
   */
  createPurchaseOrderRevision(userId, purchaseOrderId, data) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/revision`;
    return Api.post(url, { data });
  },

  /**
   * Makes call to api endpoint to join PO chat
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   */
  joinPoChat(userId, purchaseOrderId) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/join-chat`;
    return Api.post(url);
  },

  /**
   * Makes call to api endpoint to send the PO invoice to NetSuite
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   *
   * @returns {promise} promise returned from the api call
   */
  sendNSInvoice(userId, purchaseOrderId) {
    const url = `${USERS}/${userId}/purchase-orders/${purchaseOrderId}/ns-send-invoice`;
    return Api.post(url);
  },

  /**
   * Makes call to api endpoint to update the PDF timestamp on a PO
   * @param {string} userId the current user's ID
   * @param {string} purchaseOrderId the purchase order ID
   * @param {"invoice_timestamp" | "packing_list_vendor_timestamp"} type the type of PDF being updated
   *
   * @returns {promise} promise returned from the api call
   */
  updatePDFTimestamp(userId, purchaseOrderId, type) {
    const timestampUpdateUrl = `/users/${userId}/purchase-orders/${purchaseOrderId}/pdf-update`;
    const data = {
      type,
      timestamp: new Date().toISOString(),
    };
    return Api.post(timestampUpdateUrl, {
      data,
    });
  },

  /**
   * Makes call to api endpoint to get public files for vendor application
   */
  async getPublicFilesVendorApp() {
    const url = `/users/public-files`;
    const response = await Api.get(url);
    return response;
  },

  /**
   * Makes call to api endpoint to get all bill payments for the current user
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  billPayments: [BPobject1, BPobject2, ...]
   * }
   */
  getBillPayments(userId, params) {
    const url = `${USERS}/${userId}/bill-payments?${params}`;
    return Api.get(url);
  },

  /**
   * Makes call to api endpoint to get specified bill payment
   * @param {string} userId the current user's ID
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  billPayments: [BPobject1, BPobject2, ...]
   * }
   */
  getBillPayment(userId, billPaymentId) {
    const url = `${USERS}/${userId}/bill-payments/${billPaymentId}`;
    return Api.get(url);
  },

  /**
   * Get bill payments for export
   *
   * @param userId
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of bill payments.
   *
   * example: billPayments: [billPayment1, billPayment2, ...]
   */
  async getBillPaymentsExport(userId) {
    const limit = 100;
    const firstPage = await Api.get(
      `${USERS}/${userId}/bill-payments?${queryString.encode({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const payments = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return Api.get(`${USERS}/${userId}/bill-payments?${params}`);
      }),
    );

    return flatMap(payments, 'billPayments');
  },

  /**
   * Makes a call to the APi to create the vendor contact
   *
   * @param userId {integer}
   * @param contact {object}
   */
  async createVendorContact(userId, contact, reInstanciateDeletedUser) {
    return Api.post(`${USERS}/${userId}/vendor/contacts`, {
      data: { ...contact, reInstanciateDeletedUser },
    });
  },

  /**
   * Makes a call to the API to update the vendor contact
   *
   * @param userId {integer}
   * @param contactId {integer}
   * @param updates {object}
   */
  async updateVendorContact(userId, contactId, updates) {
    return Api.put(`${USERS}/${userId}/vendor/contacts/${contactId}`, {
      data: updates,
    });
  },

  /**
   * Makes a call to the API to update the vendor contact
   *
   * @param userId {integer}
   * @param contactId {integer}
   */
  async removeVendorContact(userId, contactId) {
    return Api.delete(`${USERS}/${userId}/vendor/contacts/${contactId}`);
  },

  /**
   * Makes a call to the API to create a delivery
   * @param {string} userId the user id
   * @param {object} delivery the delivery data
   * @param {array} purchaseOrderNumbers the purchase order ids to be delivered
   */
  createVendorDelivery(userId, delivery, purchaseOrderNumbers) {
    const data = { delivery, purchaseOrderNumbers };
    return Api.post(`${USERS}/${userId}/deliveries`, {
      data,
    });
  },

  /**
   * Makes a call to the API to get a delivery
   * @param {string} userId the user id
   * @param {string} deliveryId the delivery id
   */
  getVendorDelivery(userId, deliveryId) {
    return Api.get(`${USERS}/${userId}/deliveries/${deliveryId}`);
  },

  /**
   * Get vendor deliveries for export
   *
   * @param userId
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of deliveries.
   *
   * example: deliveries: [delivery1, delivery2, ...]
   */
  async getVendorDeliveriesExport(userId, options) {
    const limit = 100;
    const firstPage = await Api.get(
      `${USERS}/${userId}/deliveries?${queryString.encode(
        assign(
          {
            page: 1,
          },
          options,
        ),
      )}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const deliveries = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return Api.get(`${USERS}/${userId}/deliveries?${params}`);
      }),
    );

    return flatMap(deliveries, 'deliveries');
  },

  /**
   * Makes a call to the API to get all deliveries associated with user
   * @param {string} userId the user id
   * @param {object} params parameters for deliveries
   */
  getVendorDeliveries(userId, params) {
    return Api.get(`${USERS}/${userId}/deliveries?${params}`);
  },

  /**
   * Makes a call to the API to update a delivery
   * @param {string} userId the user id
   * @param {string} deliveryId the delivery id
   * @param {object} update the updated data
   * @param {array} purchaseOrderNumbers the purchase order numbers to be delivered
   */
  updateVendorDelivery(userId, deliveryId, update, purchaseOrderNumbers) {
    const data = {
      delivery: omit(update, 'purchase_orders'),
      purchaseOrderNumbers,
    };
    return Api.put(`${USERS}/${userId}/deliveries/${deliveryId}`, { data });
  },

  /**
   * Makes a call to the API to delete a delivery
   * @param {string} userId the user id
   * @param {string} deliveryId the delivery id
   */
  removeVendorDelivery(userId, deliveryId, reason) {
    const params = `cancellation_reason=${reason}`;
    return Api.delete(`${USERS}/${userId}/deliveries/${deliveryId}?${params}`);
  },

  /**
   * Makes a call to the API to get bill credits
   * @param {string} userId the user id
   * @param {string} params parameters for bill credits
   */
  getBillCredits(userId, params) {
    const url = `${USERS}/${userId}/bill-credits?${params}`;
    return Api.get(url);
  },

  /**
   * Makes a call to the API to get bill credit
   * @param {string} userId the user id
   * @param {string} billCreditId the id of the bill Credit
   */
  getBillCredit(userId, billCreditId) {
    const url = `${USERS}/${userId}/bill-credits/${billCreditId}`;
    return Api.get(url);
  },

  /**
   * Get bill credits for export
   * @param {string} userId the user id
   * @param {string} billCreditId the id of the bill Credit
   */
  async getBillCreditsExport(userId) {
    const limit = 100;
    const firstPage = await Api.get(
      `${USERS}/${userId}/bill-credits?${queryString.encode({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const credits = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return Api.get(`${USERS}/${userId}/bill-credits?${params}`);
      }),
    );

    return flatMap(credits, 'billCredits');
  },

  /**
   * Makes a call to the API to create vendor attributes
   * @param {string} vendorAttributes the attributes
   */
  createVendorAttributes(vendorAttributes) {
    const data = vendorAttributes;
    return Api.post('/vendor-attributes-form', { data });
  },

  /**
   * Makes a call to the API to update vendor attributes
   * @param {string} update the update
   */
  updateVendorAttributes(userId, update) {
    const data = update;
    return Api.put(`${USERS}/${userId}/vendor-attributes`, { data });
  },

  // this was used to get analytics for the old dashboard
  getDashboardAnalytics(userId) {
    return Api.get(`${USERS}/${userId}/dashboard-analytics`);
  },

  /**
   * Creates a pandadoc embed link
   *
   * @param documentId
   * @returns {*}
   */
  createDocumentLink(userId, documentId) {
    return Api.post(`${USERS}/${userId}/vendor-documents/${documentId}/link`);
  },

  /**
   * Get a list of buyers
   */
  getBuyers() {
    return Api.get(`${USERS}/buyers`);
  },

  /**
   * Get a list of sponsors
   */
  getSponsors() {
    return Api.get(`${USERS}/sponsors`);
  },
};

export default UserAPI;
